import React from 'react';

import type { LucideIcon } from 'lucide-react';
import {
  Ban,
  CircleCheck,
  CircleDashed,
  CircleDot,
  Clock,
  Eye,
  LayoutTemplate,
  MessageSquareWarning,
} from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { getAssignmentStateText, TASK_STATUS_LABEL } from './task-review-helpers';

export interface PillTone {
  background: string;
  border: string;
  text: string;
}

// Soft tint, a slightly stronger border and dark text, so every tone reads at the same weight.
export const TONE = {
  blue: { background: '#EFF8FF', border: '#B2DDFF', text: '#175CD3' },
  gray: { background: '#F9FAFB', border: '#EAECF0', text: '#475467' },
  green: { background: '#ECFDF3', border: '#ABEFC6', text: '#067647' },
  orange: { background: '#FFFAEB', border: '#FEDF89', text: '#B54708' },
  red: { background: '#FEF3F2', border: '#FECDCA', text: '#B42318' },
} satisfies Record<string, PillTone>;

const STATUS_TONE = {
  [Apollo.TaskStatus.ChangesRequested]: TONE.orange,
  [Apollo.TaskStatus.Completed]: TONE.green,
  [Apollo.TaskStatus.InReview]: TONE.blue,
  [Apollo.TaskStatus.NotStarted]: TONE.gray,
  [Apollo.TaskStatus.Rejected]: TONE.red,
  [Apollo.TaskStatus.Started]: TONE.blue,
  [Apollo.TaskStatus.Template]: TONE.gray,
} satisfies Record<Apollo.TaskStatus, PillTone>;

const STATUS_ICON = {
  [Apollo.TaskStatus.ChangesRequested]: MessageSquareWarning,
  [Apollo.TaskStatus.Completed]: CircleCheck,
  [Apollo.TaskStatus.InReview]: Eye,
  [Apollo.TaskStatus.NotStarted]: CircleDashed,
  [Apollo.TaskStatus.Rejected]: Ban,
  [Apollo.TaskStatus.Started]: CircleDot,
  [Apollo.TaskStatus.Template]: LayoutTemplate,
} satisfies Record<Apollo.TaskStatus, LucideIcon>;

const DECISION_TONE = {
  [Apollo.TaskAssigneeDecision.ChangesRequested]: TONE.orange,
  [Apollo.TaskAssigneeDecision.Completed]: TONE.green,
  [Apollo.TaskAssigneeDecision.Rejected]: TONE.red,
} satisfies Record<Apollo.TaskAssigneeDecision, PillTone>;

const DECISION_ICON = {
  [Apollo.TaskAssigneeDecision.ChangesRequested]: MessageSquareWarning,
  [Apollo.TaskAssigneeDecision.Completed]: CircleCheck,
  [Apollo.TaskAssigneeDecision.Rejected]: Ban,
} satisfies Record<Apollo.TaskAssigneeDecision, LucideIcon>;

const PILLED_STATUSES: ReadonlySet<Apollo.TaskStatus> = new Set([
  Apollo.TaskStatus.ChangesRequested,
  Apollo.TaskStatus.InReview,
  Apollo.TaskStatus.Rejected,
]);

const useStyles = makeStyles<{ tone: PillTone }>()((theme, { tone }) => ({
  pill: {
    alignItems: 'center',
    backgroundColor: tone.background,
    border: `1px solid ${tone.border}`,
    borderRadius: 10,
    color: tone.text,
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
    gap: 4,
    // 18px plus the 1px border keeps the pill 20px tall.
    lineHeight: '18px',
    padding: theme.spacing(0, 1),
    whiteSpace: 'nowrap',
  },
}));

const useBadgeStyles = makeStyles()(theme => ({
  badge: {
    borderRadius: 12,
    gap: theme.spacing(0.75),
    // 22px plus the 1px border keeps the badge 24px tall.
    lineHeight: '22px',
    padding: theme.spacing(0, 1.25, 0, 1),
  },
}));

const useStateIconStyles = makeStyles()({
  // As tall as a row's icon buttons, so rows line up whether or not they have actions.
  icon: {
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    height: 28,
    justifyContent: 'center',
    width: 28,
  },
});

interface PillProps {
  label: string;
  tone: PillTone;
  className?: string;
  icon?: React.ReactNode;
}

export function Pill({ className, icon, label, tone }: PillProps): JSX.Element {
  const { classes, cx } = useStyles({ tone });
  return (
    <span className={cx(classes.pill, className)}>
      {icon}
      {label}
    </span>
  );
}

/** The task's status with its icon, for the top of the task panel. */
export function TaskStatusBadge({ status }: { status: Apollo.TaskStatus }): JSX.Element {
  const { classes } = useBadgeStyles();
  const Icon = STATUS_ICON[status];
  return (
    <Pill
      className={classes.badge}
      icon={<Icon size={14} strokeWidth={2.25} />}
      label={TASK_STATUS_LABEL[status]}
      tone={STATUS_TONE[status]}
    />
  );
}

/** Shown only for the review states a row can't otherwise convey. */
export function TaskReviewStatusPill({
  className,
  status,
}: {
  status: Apollo.TaskStatus;
  className?: string;
}): JSX.Element | null {
  if (!PILLED_STATUSES.has(status)) return null;
  return (
    <Pill className={className} label={TASK_STATUS_LABEL[status]} tone={STATUS_TONE[status]} />
  );
}

/**
 * One row's state as an icon, with what happened, who and when in its tooltip. A reviewer shows
 * Reviewing once asked, then Approved or Changes requested, and nothing before they are asked. An
 * assignee on a task with reviewers shows nothing while the task is open, since review state lives
 * on the reviewers, and Completed once the task is; on a task without reviewers it shows Completed
 * once they finish.
 */
export function AssignmentStateIcon({
  decidedAt,
  decision,
  hasReviewers,
  name,
  requestedText,
  reviewRequestedAt,
  role,
  taskCompletedAt,
}: {
  decidedAt: string | null;
  decision: Apollo.TaskAssigneeDecision | null;
  hasReviewers: boolean;
  name: string;
  requestedText: string | null;
  reviewRequestedAt: string | null;
  role: Apollo.TaskAssigneeRole;
  taskCompletedAt: string | null;
}): JSX.Element | null {
  const { classes } = useStateIconStyles();
  const isReviewer = role === Apollo.TaskAssigneeRole.Reviewer;
  const isReviewedAssignee = !isReviewer && hasReviewers;
  if (isReviewedAssignee && !taskCompletedAt) return null;
  if (!decision && !isReviewedAssignee && !(isReviewer && reviewRequestedAt)) return null;

  const shownDecision = isReviewedAssignee ? Apollo.TaskAssigneeDecision.Completed : decision;
  const Icon = shownDecision ? DECISION_ICON[shownDecision] : Clock;
  const color = (shownDecision ? DECISION_TONE[shownDecision] : TONE.blue).text;
  const text = getAssignmentStateText({
    decidedAt,
    decision: isReviewedAssignee && decision !== shownDecision ? null : decision,
    name,
    requestedText,
    role,
    taskCompletedAt,
  });
  return (
    <AlineTooltip arrow placement="top" title={text}>
      <span aria-label={text} className={classes.icon} role="img" tabIndex={0}>
        <Icon color={color} size={16} strokeWidth={2.25} />
      </span>
    </AlineTooltip>
  );
}

export function NeutralPill({ label }: { label: string }): JSX.Element {
  return <Pill label={label} tone={TONE.gray} />;
}
